import React from "react";
import { Box, Typography, Divider } from "@mui/material";
import {
  TracingWrapper,
  OrderInfoRow,
  OrderInfoLabel,
  OrderInfoValue,
  MapViewMainContainer,
  AnimatedIconContainer,
  MapViewSubtitle,
  MapViewDescription,
  MapViewTitleDescriptionBox,
  MapViewOrderDetailsSection,
  MapViewSupportHeader,
  MapViewSupportList,
  MapViewSupportItem,
  MapViewOrderDetailsTitle,
  MapViewOrderDetailsContainer,
  // Cancelled-specific styles
  CancelledIconBox,
  CancelledTitle,
  CancelledReasonCard,
  CancelledReasonLabel,
  CancelledReasonText,
  CancelledSupportCard,
  CancelledSupportTitle,
  CancelledSupportDescription,
  CancelledSupportCaption
} from "layout/styles";
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PhoneIcon from '@mui/icons-material/Phone';
import ReceiptIcon from '@mui/icons-material/Receipt';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RestoreIcon from '@mui/icons-material/Restore';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const CancelledOrderView = ({ orderInfo = {}, isProviderCancelled, canceledByDelivery }) => {
  return (
    <TracingWrapper>
      <MapViewMainContainer>
        {/* Cancelled Icon with Animation */}
        <AnimatedIconContainer>
          <CancelledIconBox>
            <CancelIcon sx={{ 
              fontSize: 60, 
              color: 'white',
            }} />
          </CancelledIconBox>
        </AnimatedIconContainer>

        {/* Title and Main Message */}
        <MapViewTitleDescriptionBox>
          <CancelledTitle variant="h3">
            {canceledByDelivery ? 'Delivery Cancelled' : 'Order Cancelled'}
          </CancelledTitle>

          <MapViewDescription variant="body1">
            {canceledByDelivery ? 
            'The delivery for your order has been cancelled. The order itself is still active.' 
            : 'You can place a new order anytime. No charges have been applied to your account'}
          </MapViewDescription>
        </MapViewTitleDescriptionBox>

        {/* Cancellation Reason Card (if available) */}
        {(orderInfo?.rejectedReason) && (
          <CancelledReasonCard>
            <CancelledReasonLabel variant="subtitle2">
              Cancellation Reason:
            </CancelledReasonLabel>
            <CancelledReasonText variant="body2">
              {orderInfo?.rejectedReason || ''}
            </CancelledReasonText>
          </CancelledReasonCard>
        )}

        {/* Support Information */}
        <CancelledSupportCard>
          <MapViewSupportHeader>
            <SupportAgentIcon sx={{ color: 'text.primary', fontSize: 20 }} />
            <CancelledSupportTitle variant="subtitle2">
             {canceledByDelivery ? 'Delivery Successfully Cancelled' : 'Order Successfully Cancelled'}
            </CancelledSupportTitle>
          </MapViewSupportHeader>
          
          <CancelledSupportDescription variant="body2">
            {canceledByDelivery ? 
            'Your delivery has been cancelled. Please wait for further updates or contact support if needed.'
            : 'Your cancellation has been processed successfully. You can browse restaurants and place a new order anytime.'}
          </CancelledSupportDescription>

          <MapViewSupportList>
            <MapViewSupportItem>
              <PhoneIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
              <CancelledSupportCaption variant="caption">
                24/7 Customer Support Available
              </CancelledSupportCaption>
            </MapViewSupportItem>
            <MapViewSupportItem>
              <RefreshIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
              <CancelledSupportCaption variant="caption">
                {'Ready to place a new order'}
              </CancelledSupportCaption>
            </MapViewSupportItem>
            <MapViewSupportItem>
              <RestoreIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
              <CancelledSupportCaption variant="caption">
                Easy reordering from your favorites
              </CancelledSupportCaption>
            </MapViewSupportItem>
          </MapViewSupportList>
        </CancelledSupportCard>

        {/* Order Details (if needed) */}
        {(orderInfo?.number || orderInfo?.companyName) && (
          <MapViewOrderDetailsSection>
            <MapViewOrderDetailsTitle variant="h6">
              Order Details
            </MapViewOrderDetailsTitle>
            
            <MapViewOrderDetailsContainer>
              {orderInfo?.number && (
                <OrderInfoRow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon sx={{ color: 'error.main', fontSize: 18 }} />
                    <OrderInfoLabel>Order Number</OrderInfoLabel>
                  </Box>
                  <OrderInfoValue data-type="order-number">
                    #{orderInfo.number}
                  </OrderInfoValue>
                </OrderInfoRow>
              )}

              {orderInfo?.companyName && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <OrderInfoRow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StorefrontIcon sx={{ color: 'error.main', fontSize: 18 }} />
                      <OrderInfoLabel>Restaurant</OrderInfoLabel>
                    </Box>
                    <OrderInfoValue>
                      {orderInfo.companyName}
                    </OrderInfoValue>
                  </OrderInfoRow>
                </>
              )}
              <Divider sx={{ my: 0.5 }} />
              <OrderInfoRow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOnIcon sx={{ color: 'error.main', fontSize: 18 }} />
                  <OrderInfoLabel>Delivery Address</OrderInfoLabel>
                </Box>
                <OrderInfoValue sx={{ 
                  maxWidth: '300px',
                  fontSize: '16px'
                }}>
                  {orderInfo?.address || 'N/A'}
                </OrderInfoValue>
              </OrderInfoRow>

              {orderInfo?.cancelTime && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <OrderInfoRow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CancelIcon sx={{ color: 'error.main', fontSize: 18 }} />
                      <OrderInfoLabel>Cancelled At</OrderInfoLabel>
                    </Box>
                    <OrderInfoValue>
                      {new Date(orderInfo.cancelTime).toLocaleString()}
                    </OrderInfoValue>
                  </OrderInfoRow>
                </>
              )}
            </MapViewOrderDetailsContainer>
          </MapViewOrderDetailsSection>
        )}
      </MapViewMainContainer>
    </TracingWrapper>
  );
};

export default CancelledOrderView; 